import { useMemo } from 'react'

import { maxBy } from 'lodash'

import { ADDRESS_ZERO } from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useMultipleSmartAccountBalances } from '~/hooks/useBalances'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { useVaultAssets } from '~/hooks/useTokens'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { isSameToken } from '~/utils/tokens'
import { getVaultsForAsset } from '~/utils/vault'

/**
 * MAX for Send's recipient amount when the sender's largest same-chain Earn
 * position in the selected token redeems for more than their liquid balance of
 * it. Send pays from one source, so MAX is the larger of the two, never the
 * sum. Undefined leaves TokenSelector's liquid MAX in place.
 */
export const useEarnMaxOverride = ({
  sender,
  token,
  enabled,
}: {
  sender: IAddress
  token: { token?: IAddress; chainId?: SupportedChainId } | undefined
  enabled: boolean
}) => {
  const { org } = useActiveOrg()
  const { convertTokenToUSD } = useTokenPrices()
  const { balances: balancesMap } = useMultipleSmartAccountBalances([sender])
  const balances = balancesMap[sender]?.balances

  const chainId = token?.chainId
  const asset = token?.token

  // Payment sources drop networks the org turned off, so Earn there can't pay.
  const isEnabledChain =
    !!chainId && (org?.smartAccountsChainIds?.includes(chainId) ?? false)

  const vaults =
    enabled && isEnabledChain && asset ? getVaultsForAsset(chainId, asset) : []

  // Base has Aave (6-decimal) and Morpho (18-decimal) shares, so compare
  // positions by USD value rather than share amount.
  const position = maxBy(
    (balances ?? []).filter(
      (balance) =>
        balance.chainId === chainId &&
        balance.amount > 0n &&
        vaults.some((vault) => Address.equal(vault, balance.address)),
    ),
    (balance) => balance.usdValue,
  )

  // Undefined until it loads, so MAX never falls back to 0 in the meantime.
  const { data: redeemable } = useVaultAssets({
    vault: position?.address ?? ADDRESS_ZERO,
    chainId: position?.chainId ?? 1,
    balance: position?.amount ?? 0n,
    enabled: !!position,
  })

  const liquid =
    balances?.find((balance) => isSameToken(balance, token))?.amount ?? 0n

  return useMemo(() => {
    if (!position || !asset || redeemable === undefined || redeemable <= liquid)
      return undefined
    return {
      amount: redeemable,
      totalUsdValue:
        convertTokenToUSD({
          token: asset,
          amount: redeemable,
          chainId: position.chainId,
        }) ?? 0,
    }
  }, [position, asset, redeemable, liquid, convertTokenToUSD])
}
